import { useCallback, useEffect, useMemo, useState } from "react";

import { createEvent, deleteEvent, fetchEventParticipants, fetchEvents, fetchMembers, updateEvent, uploadAdminMedia } from "@/features/admin/api";
import { useAdminSession } from "@/features/admin/useAdminSession";
import type { AdminEvent, AdminEventParticipant, AdminMember } from "@/features/admin/types";
import { formatEventScheduleLine } from "@/shared/events/dateRange";
import { exportRowsToExcel } from "@/shared/export/excel";

import PaginationControls from "./PaginationControls";

const typeConfig: Record<AdminEvent["type"], { label: string; cls: string; icon: string }> = {
  match: { label: "Match", cls: "bg-orange-100 text-[#E8521A]", icon: "ri-football-line" },
  entrainement: { label: "Entraînement", cls: "bg-teal-100 text-[#1A8C7A]", icon: "ri-run-line" },
  tournoi: { label: "Tournoi", cls: "bg-amber-100 text-amber-600", icon: "ri-trophy-line" },
  culture: { label: "Rubrique enfant", cls: "bg-emerald-100 text-emerald-600", icon: "ri-parent-line" },
  reunion_bureau: { label: "Réunion bureau", cls: "bg-sky-100 text-sky-700", icon: "ri-calendar-check-line" },
  general: { label: "Général", cls: "bg-gray-100 text-gray-600", icon: "ri-calendar-line" }
};
const EVENTS_PER_PAGE = 5;

function toDaySortValue(date: string) {
  return new Date(`${date}T12:00:00`).getTime();
}

function normalizeMeetingUrl(value: string) {
  const trimmed = value.trim();
  return trimmed || null;
}

function isValidMeetingUrl(value: string) {
  const trimmed = value.trim();

  if (!trimmed) {
    return true;
  }

  try {
    const url = new URL(trimmed);
    return url.protocol === "http:" || url.protocol === "https:";
  } catch {
    return false;
  }
}

function formatParticipantDate(value: string) {
  return new Date(value).toLocaleString("fr-FR", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit"
  });
}

export default function EvenementsTab() {
  const { request } = useAdminSession();
  const [events, setEvents] = useState<AdminEvent[]>([]);
  const [showForm, setShowForm] = useState(false);
  const [editEvent, setEditEvent] = useState<AdminEvent | null>(null);
  const [toast, setToast] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [formError, setFormError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [imageMode, setImageMode] = useState<"url" | "upload">("upload");
  const [selectedImageFile, setSelectedImageFile] = useState<File | null>(null);
  const [imagePreviewUrl, setImagePreviewUrl] = useState<string | null>(null);
  const [imageInputKey, setImageInputKey] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [participantsEvent, setParticipantsEvent] = useState<AdminEvent | null>(null);
  const [participants, setParticipants] = useState<AdminEventParticipant[]>([]);
  const [participantsLoading, setParticipantsLoading] = useState(false);
  const [participantsError, setParticipantsError] = useState<string | null>(null);
  const [meetingMembers, setMeetingMembers] = useState<AdminMember[]>([]);
  const [meetingMemberSearch, setMeetingMemberSearch] = useState("");
  const [meetingMembersLoading, setMeetingMembersLoading] = useState(false);
  const [selectedMeetingMemberIds, setSelectedMeetingMemberIds] = useState<string[]>([]);
  const [form, setForm] = useState({
    titre: "",
    type: "match" as AdminEvent["type"],
    date: "",
    heure: "",
    dateFin: "",
    heureFin: "",
    lieu: "",
    maxParticipants: "",
    description: "",
    image: "",
    meetingUrl: ""
  });

  const loadEvents = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const nextEvents = await fetchEvents(request);
      setEvents(nextEvents);
    } catch (caughtError) {
      const message = caughtError instanceof Error ? caughtError.message : "Impossible de charger les événements";
      setError(message);
    } finally {
      setLoading(false);
    }
  }, [request]);

  useEffect(() => {
    void loadEvents();
  }, [loadEvents]);

  const showToast = (message: string) => {
    setToast(message);
    window.setTimeout(() => setToast(null), 3000);
  };

  const isChildrenEventForm = form.type === "culture";
  const isBureauMeetingForm = form.type === "reunion_bureau";
  const isLockedTypeForm = isChildrenEventForm || isBureauMeetingForm;

  const loadMeetingMembers = useCallback(async () => {
    setMeetingMembersLoading(true);

    try {
      const members = await fetchMembers(request, {
        search: meetingMemberSearch.trim(),
        status: "actif"
      });
      setMeetingMembers(members);
    } catch {
      setMeetingMembers([]);
    } finally {
      setMeetingMembersLoading(false);
    }
  }, [meetingMemberSearch, request]);

  useEffect(() => {
    if (!showForm || !isBureauMeetingForm) {
      return;
    }

    void loadMeetingMembers();
  }, [isBureauMeetingForm, loadMeetingMembers, showForm]);

  const openNew = () => {
    setEditEvent(null);
    setForm({ titre: "", type: "match", date: "", heure: "", dateFin: "", heureFin: "", lieu: "", maxParticipants: "", description: "", image: "", meetingUrl: "" });
    setFormError(null);
    setImageMode("upload");
    setSelectedImageFile(null);
    setImagePreviewUrl(null);
    setImageInputKey((current) => current + 1);
    setSelectedMeetingMemberIds([]);
    setMeetingMemberSearch("");
    setShowForm(true);
  };

  const openChildrenCategory = () => {
    setEditEvent(null);
    setForm({ titre: "", type: "culture", date: "", heure: "", dateFin: "", heureFin: "", lieu: "", maxParticipants: "", description: "", image: "", meetingUrl: "" });
    setFormError(null);
    setImageMode("upload");
    setSelectedImageFile(null);
    setImagePreviewUrl(null);
    setImageInputKey((current) => current + 1);
    setSelectedMeetingMemberIds([]);
    setMeetingMemberSearch("");
    setShowForm(true);
  };

  const openBureauMeeting = () => {
    setEditEvent(null);
    setForm({
      titre: "Prochaine réunion du bureau",
      type: "reunion_bureau",
      date: "",
      heure: "",
      dateFin: "",
      heureFin: "",
      lieu: "",
      maxParticipants: "20",
      description: "Réunion du bureau ASC Sonatel.",
      image: "",
      meetingUrl: ""
    });
    setFormError(null);
    setImageMode("upload");
    setSelectedImageFile(null);
    setImagePreviewUrl(null);
    setImageInputKey((current) => current + 1);
    setSelectedMeetingMemberIds([]);
    setMeetingMemberSearch("");
    setShowForm(true);
  };

  const openEdit = (event: AdminEvent) => {
    setEditEvent(event);
    setFormError(null);
    setForm({
      titre: event.titre,
      type: event.type,
      date: event.date,
      heure: event.heure,
      dateFin: event.dateFin ?? event.date,
      heureFin: event.heureFin ?? event.heure,
      lieu: event.lieu,
      maxParticipants: String(event.maxParticipants),
      description: event.description,
      image: event.image ?? "",
      meetingUrl: event.meetingUrl ?? ""
    });
    setImageMode(event.type === "culture" ? "url" : "upload");
    setSelectedImageFile(null);
    setImagePreviewUrl(event.type === "culture" ? (event.image ?? null) : null);
    setImageInputKey((current) => current + 1);
    setSelectedMeetingMemberIds(event.type === "reunion_bureau" ? event.meetingParticipantIds ?? [] : []);
    setMeetingMemberSearch("");
    setShowForm(true);
  };

  useEffect(() => {
    if (!selectedImageFile) {
      if (!showForm) {
        setImagePreviewUrl(null);
      }
      return;
    }

    const preview = URL.createObjectURL(selectedImageFile);
    setImagePreviewUrl(preview);

    return () => {
      URL.revokeObjectURL(preview);
    };
  }, [selectedImageFile, showForm]);

  const handleSave = async () => {
    setFormError(null);

    if (!form.titre.trim() || !form.date || !form.heure || !form.dateFin || !form.heureFin || (!isBureauMeetingForm && !form.lieu.trim()) || !form.description.trim()) {
      setFormError(`Renseigne le titre, la date/heure de début, la date/heure de fin${isBureauMeetingForm ? "" : ", le lieu"} et la description pour enregistrer ${isBureauMeetingForm ? "la réunion" : "l'événement"}.`);
      return;
    }

    if (new Date(`${form.dateFin}T${form.heureFin}:00`).getTime() < new Date(`${form.date}T${form.heure}:00`).getTime()) {
      setFormError("La date de fin doit être supérieure ou égale à la date de début.");
      return;
    }

    if (isBureauMeetingForm && !isValidMeetingUrl(form.meetingUrl)) {
      setFormError("Renseigne un lien de réunion valide, par exemple un lien Google Meet, Zoom ou Teams.");
      return;
    }

    if (isBureauMeetingForm && selectedMeetingMemberIds.length === 0) {
      setFormError("Sélectionne au moins un membre qui participera à cette réunion.");
      return;
    }

    try {
      let resolvedImage = isChildrenEventForm ? form.image.trim() || editEvent?.image || "" : "";

      if (isChildrenEventForm && imageMode === "upload" && selectedImageFile) {
        const [asset] = await uploadAdminMedia(request, [selectedImageFile], "asc-sonatel/events");
        resolvedImage = asset?.url ?? resolvedImage;
      }

      const payload = {
        titre: form.titre,
        type: form.type,
        date: form.date,
        heure: form.heure,
        dateFin: form.dateFin,
        heureFin: form.heureFin,
        lieu: isBureauMeetingForm ? form.lieu.trim() || "En ligne" : form.lieu.trim(),
        maxParticipants: Number(form.maxParticipants) || editEvent?.maxParticipants || 50,
        description: form.description,
        ...(isBureauMeetingForm ? { meetingUrl: normalizeMeetingUrl(form.meetingUrl) } : {}),
        ...(isBureauMeetingForm ? { meetingParticipantIds: selectedMeetingMemberIds } : {}),
        ...(isChildrenEventForm && resolvedImage ? { image: resolvedImage } : {})
      };

      if (editEvent) {
        await updateEvent(request, editEvent.id, payload);
        showToast(isBureauMeetingForm ? "Réunion mise à jour" : "Événement mis à jour");
      } else {
        await createEvent(request, {
          ...payload,
          statut: "publie"
        });
        showToast(isBureauMeetingForm ? "Réunion planifiée" : "Événement créé");
      }

      setShowForm(false);
      setFormError(null);
      setSelectedImageFile(null);
      setImagePreviewUrl(null);
      setImageInputKey((current) => current + 1);
      setSelectedMeetingMemberIds([]);
      setMeetingMemberSearch("");
      await loadEvents();
    } catch (caughtError) {
      const message = caughtError instanceof Error ? caughtError.message : "Enregistrement impossible";
      setFormError(message);
    }
  };

  const handleDelete = async (id: string) => {
    try {
      await deleteEvent(request, id);
      showToast("Événement supprimé");
      await loadEvents();
    } catch (caughtError) {
      const message = caughtError instanceof Error ? caughtError.message : "Suppression impossible";
      setError(message);
    }
  };

  const handleCancel = async (id: string) => {
    try {
      await updateEvent(request, id, { statut: "annule" });
      showToast("Événement annulé");
      await loadEvents();
    } catch (caughtError) {
      const message = caughtError instanceof Error ? caughtError.message : "Action impossible";
      setError(message);
    }
  };

  const openParticipants = async (event: AdminEvent) => {
    setParticipantsEvent(event);
    setParticipants([]);
    setParticipantsError(null);
    setParticipantsLoading(true);

    try {
      const nextParticipants = await fetchEventParticipants(request, event.id);
      setParticipants(nextParticipants);
    } catch (caughtError) {
      const message = caughtError instanceof Error ? caughtError.message : "Impossible de charger les participants.";
      setParticipantsError(message);
    } finally {
      setParticipantsLoading(false);
    }
  };

  const exportParticipants = async () => {
    if (!participantsEvent) {
      return;
    }

    const rows = participants.map((participant) => [
      ["Événement", participantsEvent.titre],
      ["Type", participant.type === "child" ? "Enfant" : "Membre"],
      ["Nom", participant.nom],
      ["Enfant", participant.enfant ?? ""],
      ["Email", participant.email ?? ""],
      ["Téléphone", participant.telephone ?? ""],
      ["Direction", participant.departement ?? ""],
      ["Matricule", participant.matricule ?? ""],
      ["Objectif", participant.objectif ?? ""],
      ["Inscrit le", formatParticipantDate(participant.inscritLe)]
    ]).map((entries) => Object.fromEntries(entries));

    await exportRowsToExcel(`participants-${participantsEvent.titre}`, "Participants", rows);
  };

  const todaySortValue = toDaySortValue(new Date().toISOString().split("T")[0] ?? "");
  const upcoming = useMemo(
    () => events.filter((event) => event.statut !== "annule" && toDaySortValue(event.date) >= todaySortValue),
    [events, todaySortValue]
  );
  const past = useMemo(
    () => events.filter((event) => event.statut !== "annule" && toDaySortValue(event.date) < todaySortValue),
    [events, todaySortValue]
  );
  const cancelled = useMemo(() => events.filter((event) => event.statut === "annule"), [events]);
  const totalPages = Math.max(1, Math.ceil(events.length / EVENTS_PER_PAGE));
  const paginatedEvents = useMemo(() => {
    const startIndex = (currentPage - 1) * EVENTS_PER_PAGE;
    return events.slice(startIndex, startIndex + EVENTS_PER_PAGE);
  }, [currentPage, events]);

  useEffect(() => {
    setCurrentPage((page) => Math.min(page, totalPages));
  }, [totalPages]);

  return (
    <div className="space-y-6 relative">
      {toast && (
        <div className="fixed top-6 right-6 z-50 px-5 py-3 rounded-xl shadow-lg bg-emerald-500 text-white text-sm font-medium flex items-center gap-2">
          <i className="ri-checkbox-circle-line text-lg"></i>
          {toast}
        </div>
      )}

      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h1 className="text-xl md:text-2xl font-bold text-[#1A1A1A]">Gestion des Événements</h1>
          <p className="text-gray-500 text-sm mt-1">Créez et gérez les événements sportifs, culturels et les réunions du bureau</p>
          {error && <p className="text-red-500 text-sm mt-2">{error}</p>}
        </div>
        <div className="flex flex-wrap gap-2 self-start sm:self-auto">
          <button
            onClick={openChildrenCategory}
            className="flex items-center gap-2 px-4 py-2.5 rounded-xl bg-emerald-600 text-white text-sm font-medium hover:bg-emerald-700 transition-colors whitespace-nowrap"
          >
            <i className="ri-parent-line"></i>
            Rubrique enfant
          </button>
          <button
            onClick={openBureauMeeting}
            className="flex items-center gap-2 px-4 py-2.5 rounded-xl bg-sky-600 text-white text-sm font-medium hover:bg-sky-700 transition-colors whitespace-nowrap"
          >
            <i className="ri-calendar-check-line"></i>
            Planifier une réunion
          </button>
          <button
            onClick={openNew}
            className="flex items-center gap-2 px-4 py-2.5 rounded-xl bg-[#E8521A] text-white text-sm font-medium hover:bg-[#d44a16] transition-colors whitespace-nowrap"
          >
            <i className="ri-add-line"></i>
            Nouvel événement
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div className="bg-white rounded-2xl p-4 border border-gray-100">
          <div className="text-2xl font-bold text-[#E8521A]">{upcoming.length}</div>
          <div className="text-sm text-gray-500 mt-1">À venir</div>
        </div>
        <div className="bg-white rounded-2xl p-4 border border-gray-100">
          <div className="text-2xl font-bold text-gray-400">{past.length}</div>
          <div className="text-sm text-gray-500 mt-1">Passés</div>
        </div>
        <div className="bg-white rounded-2xl p-4 border border-gray-100">
          <div className="text-2xl font-bold text-red-400">{cancelled.length}</div>
          <div className="text-sm text-gray-500 mt-1">Annulés</div>
        </div>
      </div>

      <div className="space-y-3">
        {loading ? (
          Array.from({ length: 4 }).map((_, index) => (
            <div key={index} className="bg-white rounded-2xl border border-gray-100 p-5 h-32 animate-pulse"></div>
          ))
        ) : paginatedEvents.map((event) => {
          const type = typeConfig[event.type];
          const isCancelled = event.statut === "annule";
          const isPast = new Date(event.date) < new Date();
          const fillPct = event.maxParticipants > 0 ? Math.round((event.participants / event.maxParticipants) * 100) : 0;
          const hasEventImage = Boolean(event.image);

          return (
            <div key={event.id} className={`bg-white rounded-2xl border p-5 transition-colors ${isCancelled ? "border-red-100 opacity-60" : "border-gray-100 hover:border-orange-200"}`}>
              <div className="flex items-start justify-between gap-4">
                <div className="flex items-start gap-4 flex-1 min-w-0">
                  <div className="h-16 w-16 overflow-hidden rounded-2xl border border-gray-100 bg-white flex-shrink-0">
                    {hasEventImage ? (
                      <img src={event.image} alt={event.titre} className="h-full w-full object-cover" />
                    ) : (
                      <div className={`flex h-full w-full items-center justify-center ${type.cls}`}>
                        <i className={`${type.icon} text-xl`}></i>
                      </div>
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap mb-1">
                      <h3 className="text-sm font-bold text-[#1A1A1A]">{event.titre}</h3>
                      <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${type.cls}`}>{type.label}</span>
                      {isCancelled ? <span className="text-xs bg-red-100 text-red-500 px-2 py-0.5 rounded-full font-medium">Annulé</span> : null}
                      {isPast && !isCancelled ? <span className="text-xs bg-gray-100 text-gray-500 px-2 py-0.5 rounded-full font-medium">Terminé</span> : null}
                    </div>
                    <div className="flex flex-wrap gap-4 text-xs text-gray-500 mb-2">
                      <span className="flex items-center gap-1"><i className="ri-calendar-line"></i>{formatEventScheduleLine(event)}</span>
                      <span className="flex items-center gap-1"><i className="ri-map-pin-line"></i>{event.lieu}</span>
                      {event.type === "reunion_bureau" && event.meetingUrl ? (
                        <a
                          href={event.meetingUrl}
                          target="_blank"
                          rel="noreferrer"
                          className="flex items-center gap-1 text-sky-600 hover:text-sky-700 hover:underline"
                        >
                          <i className="ri-video-chat-line"></i>
                          Lien réunion
                        </a>
                      ) : null}
                    </div>
                    <div className="flex items-center gap-3">
                      <div className="flex-1 max-w-[200px]">
                        <div className="flex justify-between text-xs text-gray-400 mb-1">
                          <span>{event.participants} participants</span>
                          <span>{event.maxParticipants} max</span>
                        </div>
                        <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden">
                          <div
                            className={`h-full rounded-full ${fillPct >= 90 ? "bg-red-400" : fillPct >= 60 ? "bg-amber-400" : "bg-[#1A8C7A]"}`}
                            style={{ width: `${fillPct}%` }}
                          ></div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                {!isCancelled ? (
                  <div className="flex gap-1.5 flex-shrink-0">
                    <button
                      type="button"
                      onClick={() => void openParticipants(event)}
                      title="Voir les participants"
                      className="w-8 h-8 flex items-center justify-center rounded-lg bg-sky-50 text-sky-600 hover:bg-sky-100 transition-colors"
                    >
                      <i className="ri-eye-line text-sm"></i>
                    </button>
                    <button
                      onClick={() => openEdit(event)}
                      className="w-8 h-8 flex items-center justify-center rounded-lg bg-gray-100 text-gray-600 hover:bg-orange-100 hover:text-[#E8521A] transition-colors"
                    >
                      <i className="ri-edit-line text-sm"></i>
                    </button>
                    <button
                      onClick={() => void handleCancel(event.id)}
                      className="w-8 h-8 flex items-center justify-center rounded-lg bg-amber-100 text-amber-600 hover:bg-amber-200 transition-colors"
                    >
                      <i className="ri-close-line text-sm"></i>
                    </button>
                    <button
                      onClick={() => void handleDelete(event.id)}
                      className="w-8 h-8 flex items-center justify-center rounded-lg bg-red-50 text-red-400 hover:bg-red-100 transition-colors"
                    >
                      <i className="ri-delete-bin-line text-sm"></i>
                    </button>
                  </div>
                ) : null}
              </div>
            </div>
          );
        })}
      </div>

      {!loading && events.length > 0 ? (
        <PaginationControls
          currentPage={currentPage}
          totalPages={totalPages}
          onPageChange={setCurrentPage}
          itemLabel="événements"
        />
      ) : null}

      {participantsEvent && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={() => setParticipantsEvent(null)}>
          <div className="flex max-h-[90vh] w-full max-w-5xl flex-col overflow-hidden rounded-2xl bg-white shadow-2xl" onClick={(event) => event.stopPropagation()}>
            <div className="p-6 border-b border-gray-100 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
              <div>
                <h3 className="font-bold text-[#1A1A1A] text-lg">Participants</h3>
                <p className="mt-1 text-sm text-gray-500">{participantsEvent.titre}</p>
              </div>
              <div className="flex flex-wrap gap-2">
                <button
                  type="button"
                  onClick={() => void exportParticipants()}
                  disabled={participantsLoading || participants.length === 0}
                  className="inline-flex items-center justify-center gap-2 rounded-xl bg-emerald-600 px-4 py-2.5 text-sm font-semibold text-white transition hover:bg-emerald-700 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  <i className="ri-file-excel-2-line"></i>
                  Exporter Excel
                </button>
                <button onClick={() => setParticipantsEvent(null)} className="w-10 h-10 flex items-center justify-center rounded-xl border border-gray-200 hover:bg-gray-50 transition-colors">
                  <i className="ri-close-line text-gray-500"></i>
                </button>
              </div>
            </div>
            <div className="flex-1 overflow-y-auto p-6">
              {participantsError ? (
                <div className="mb-4 rounded-xl border border-red-100 bg-red-50 px-4 py-3 text-sm text-red-600">
                  {participantsError}
                </div>
              ) : null}
              <div className="overflow-hidden rounded-2xl border border-gray-100 bg-white">
                <div className="overflow-x-auto">
                  <table className="w-full min-w-[1040px]">
                    <thead>
                      <tr className="border-b border-gray-100 bg-gray-50">
                        <th className="px-4 py-3 text-left text-xs font-semibold uppercase text-gray-500">Type</th>
                        <th className="px-4 py-3 text-left text-xs font-semibold uppercase text-gray-500">Nom</th>
                        <th className="px-4 py-3 text-left text-xs font-semibold uppercase text-gray-500">Enfant</th>
                        <th className="px-4 py-3 text-left text-xs font-semibold uppercase text-gray-500">Email</th>
                        <th className="px-4 py-3 text-left text-xs font-semibold uppercase text-gray-500">Téléphone</th>
                        <th className="px-4 py-3 text-left text-xs font-semibold uppercase text-gray-500">Direction</th>
                        <th className="px-4 py-3 text-left text-xs font-semibold uppercase text-gray-500">Matricule</th>
                        <th className="px-4 py-3 text-left text-xs font-semibold uppercase text-gray-500">Objectif</th>
                        <th className="px-4 py-3 text-left text-xs font-semibold uppercase text-gray-500">Inscrit le</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-50">
                      {participantsLoading ? (
                        Array.from({ length: 3 }).map((_, index) => (
                          <tr key={index}>
                            <td colSpan={9} className="px-4 py-4">
                              <div className="h-10 animate-pulse rounded-xl bg-gray-100"></div>
                            </td>
                          </tr>
                        ))
                      ) : participants.map((participant) => (
                        <tr key={participant.id} className="hover:bg-gray-50">
                          <td className="px-4 py-3 text-sm">
                            <span className={`inline-flex rounded-full px-2.5 py-1 text-xs font-semibold ${participant.type === "child" ? "bg-emerald-100 text-emerald-700" : "bg-sky-100 text-sky-700"}`}>
                              {participant.type === "child" ? "Enfant" : "Membre"}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-sm font-semibold text-[#1A1A1A]">{participant.nom}</td>
                          <td className="px-4 py-3 text-sm text-gray-600">{participant.enfant ?? "—"}</td>
                          <td className="px-4 py-3 text-sm text-gray-600">{participant.email ?? "—"}</td>
                          <td className="px-4 py-3 text-sm text-gray-600">{participant.telephone ?? "—"}</td>
                          <td className="px-4 py-3 text-sm text-gray-600">{participant.departement ?? "—"}</td>
                          <td className="px-4 py-3 text-sm text-gray-600">{participant.matricule ?? "—"}</td>
                          <td className="px-4 py-3 text-sm text-gray-600">{participant.objectif ?? "—"}</td>
                          <td className="px-4 py-3 text-sm text-gray-600">{formatParticipantDate(participant.inscritLe)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                {!participantsLoading && participants.length === 0 ? (
                  <div className="py-12 text-center text-sm text-gray-400">
                    <i className="ri-user-search-line mb-3 block text-4xl"></i>
                    Aucun participant inscrit pour cet événement.
                  </div>
                ) : null}
              </div>
            </div>
          </div>
        </div>
      )}

      {showForm && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={() => setShowForm(false)}>
          <div className="flex max-h-[90vh] w-full max-w-2xl flex-col overflow-hidden rounded-2xl bg-white shadow-2xl" onClick={(event) => event.stopPropagation()}>
            <div className="p-6 border-b border-gray-100 flex items-center justify-between">
              <h3 className="font-bold text-[#1A1A1A] text-lg">
                {editEvent ? (isBureauMeetingForm ? "Modifier la réunion" : "Modifier l'événement") : isBureauMeetingForm ? "Planifier une réunion" : "Nouvel événement"}
              </h3>
              <button onClick={() => setShowForm(false)} className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-gray-100 transition-colors">
                <i className="ri-close-line text-gray-500"></i>
              </button>
            </div>
            <div className="flex-1 overflow-y-auto p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">Titre *</label>
                <input
                  type="text"
                  value={form.titre}
                  onChange={(event) => setForm({ ...form, titre: event.target.value })}
                  placeholder={isBureauMeetingForm ? "Titre de la réunion..." : "Titre de l'événement..."}
                  className="w-full px-4 py-3 border border-gray-200 rounded-xl text-sm focus:outline-none focus:border-[#E8521A] transition-colors"
                />
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">Type</label>
                  <select
                    value={form.type}
                    onChange={(event) => setForm({ ...form, type: event.target.value as AdminEvent["type"] })}
                    className="w-full px-4 py-3 border border-gray-200 rounded-xl text-sm focus:outline-none focus:border-[#E8521A] transition-colors bg-white"
                    disabled={isLockedTypeForm}
                  >
                    {isChildrenEventForm ? <option value="culture">Rubrique enfant</option> : null}
                    {isBureauMeetingForm ? <option value="reunion_bureau">Réunion du bureau</option> : null}
                    <option value="match">Match</option>
                    <option value="entrainement">Entraînement</option>
                    <option value="tournoi">Tournoi</option>
                    <option value="general">Général</option>
                  </select>
                  {isChildrenEventForm ? (
                    <p className="mt-1 text-xs text-emerald-600">Cette création est réservée à la rubrique enfant.</p>
                  ) : null}
                  {isBureauMeetingForm ? (
                    <p className="mt-1 text-xs text-sky-600">Cette réunion alimentera la carte “Prochaine réunion du bureau”.</p>
                  ) : null}
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">{isBureauMeetingForm ? "Capacité" : "Max participants"}</label>
                  <input
                    type="number"
                    value={form.maxParticipants}
                    onChange={(event) => setForm({ ...form, maxParticipants: event.target.value })}
                    placeholder="50"
                    className="w-full px-4 py-3 border border-gray-200 rounded-xl text-sm focus:outline-none focus:border-[#E8521A] transition-colors"
                  />
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">Date de début *</label>
                  <input
                    type="date"
                    value={form.date}
                    onChange={(event) => setForm({ ...form, date: event.target.value, dateFin: form.dateFin || event.target.value })}
                    className="w-full px-4 py-3 border border-gray-200 rounded-xl text-sm focus:outline-none focus:border-[#E8521A] transition-colors"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">Heure de début *</label>
                  <input
                    type="time"
                    value={form.heure}
                    onChange={(event) => setForm({ ...form, heure: event.target.value, heureFin: form.heureFin || event.target.value })}
                    className="w-full px-4 py-3 border border-gray-200 rounded-xl text-sm focus:outline-none focus:border-[#E8521A] transition-colors"
                  />
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">Date de fin *</label>
                  <input
                    type="date"
                    value={form.dateFin}
                    min={form.date || undefined}
                    onChange={(event) => setForm({ ...form, dateFin: event.target.value })}
                    className="w-full px-4 py-3 border border-gray-200 rounded-xl text-sm focus:outline-none focus:border-[#E8521A] transition-colors"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">Heure de fin *</label>
                  <input
                    type="time"
                    value={form.heureFin}
                    onChange={(event) => setForm({ ...form, heureFin: event.target.value })}
                    className="w-full px-4 py-3 border border-gray-200 rounded-xl text-sm focus:outline-none focus:border-[#E8521A] transition-colors"
                  />
                </div>
              </div>
              {isChildrenEventForm ? (
                <div className="rounded-xl border border-orange-100 bg-orange-50 px-4 py-3 text-sm text-orange-700">
                  Cet événement sera affiché dans <span className="font-semibold">Cultures & Familles</span> pour permettre aux membres connectés d'inscrire leurs enfants.
                </div>
              ) : null}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">{isBureauMeetingForm ? "Lieu / information" : "Lieu *"}</label>
                <input
                  type="text"
                  value={form.lieu}
                  onChange={(event) => setForm({ ...form, lieu: event.target.value })}
                  placeholder={isBureauMeetingForm ? "Ex: En ligne, salle du bureau..." : "Lieu de l'événement..."}
                  className="w-full px-4 py-3 border border-gray-200 rounded-xl text-sm focus:outline-none focus:border-[#E8521A] transition-colors"
                />
                {isBureauMeetingForm ? (
                  <p className="mt-1 text-xs text-gray-400">Si ce champ reste vide, “En ligne” sera affiché.</p>
                ) : null}
              </div>
              {isBureauMeetingForm ? (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">Lien Google Meet, Zoom ou Teams</label>
                  <input
                    type="url"
                    value={form.meetingUrl}
                    onChange={(event) => setForm({ ...form, meetingUrl: event.target.value })}
                    placeholder="https://meet.google.com/..."
                    className="w-full px-4 py-3 border border-gray-200 rounded-xl text-sm focus:outline-none focus:border-[#E8521A] transition-colors"
                  />
                  <p className="mt-1 text-xs text-gray-400">Optionnel. Le lien sera envoyé par email et visible uniquement dans l'espace membre des invités.</p>
                </div>
              ) : null}
              {isBureauMeetingForm ? (
                <div className="rounded-2xl border border-sky-100 bg-sky-50/50 p-4">
                  <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                    <div>
                      <label className="block text-sm font-semibold text-gray-800">Membres invités *</label>
                      <p className="mt-1 text-xs text-gray-500">
                        Les membres sélectionnés recevront la réunion dans leur espace membre et par email.
                      </p>
                    </div>
                    <span className="inline-flex w-fit rounded-full bg-white px-3 py-1 text-xs font-semibold text-sky-700">
                      {selectedMeetingMemberIds.length} sélectionné{selectedMeetingMemberIds.length > 1 ? "s" : ""}
                    </span>
                  </div>

                  <div className="mt-4 flex flex-col gap-2 sm:flex-row">
                    <div className="relative flex-1">
                      <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"></i>
                      <input
                        type="text"
                        value={meetingMemberSearch}
                        onChange={(event) => setMeetingMemberSearch(event.target.value)}
                        placeholder="Rechercher par nom, email, matricule ou direction..."
                        className="w-full rounded-xl border border-gray-200 py-3 pl-9 pr-4 text-sm transition-colors focus:border-[#E8521A] focus:outline-none"
                      />
                    </div>
                    <button
                      type="button"
                      onClick={() => {
                        const visibleIds = meetingMembers.map((member) => member.id);
                        const allVisibleSelected = visibleIds.every((id) => selectedMeetingMemberIds.includes(id));
                        setSelectedMeetingMemberIds((current) =>
                          allVisibleSelected
                            ? current.filter((id) => !visibleIds.includes(id))
                            : Array.from(new Set([...current, ...visibleIds]))
                        );
                      }}
                      disabled={meetingMembers.length === 0}
                      className="rounded-xl border border-sky-100 bg-white px-4 py-3 text-sm font-semibold text-sky-700 transition-colors hover:bg-sky-50 disabled:cursor-not-allowed disabled:opacity-50"
                    >
                      Tout sélectionner
                    </button>
                  </div>

                  <div className="mt-4 max-h-64 overflow-y-auto rounded-2xl border border-white bg-white">
                    {meetingMembersLoading ? (
                      <div className="space-y-2 p-3">
                        {Array.from({ length: 4 }).map((_, index) => (
                          <div key={index} className="h-14 animate-pulse rounded-xl bg-gray-100"></div>
                        ))}
                      </div>
                    ) : meetingMembers.length > 0 ? (
                      <div className="divide-y divide-gray-50">
                        {meetingMembers.map((member) => {
                          const checked = selectedMeetingMemberIds.includes(member.id);

                          return (
                            <label key={member.id} className="flex cursor-pointer items-center gap-3 px-4 py-3 hover:bg-gray-50">
                              <input
                                type="checkbox"
                                checked={checked}
                                onChange={(event) => {
                                  setSelectedMeetingMemberIds((current) =>
                                    event.target.checked
                                      ? Array.from(new Set([...current, member.id]))
                                      : current.filter((id) => id !== member.id)
                                  );
                                }}
                                className="h-4 w-4 rounded border-gray-300 text-[#E8521A] focus:ring-[#E8521A]"
                              />
                              <img src={member.avatar ?? ""} alt={member.nom} className="h-10 w-10 rounded-xl object-cover" />
                              <div className="min-w-0 flex-1">
                                <div className="truncate text-sm font-semibold text-[#1A1A1A]">{member.prenom} {member.nom}</div>
                                <div className="truncate text-xs text-gray-500">{member.email ?? "Email non renseigné"}</div>
                              </div>
                              <div className="hidden text-right text-xs text-gray-400 sm:block">
                                <div>{member.matricule ?? "Sans matricule"}</div>
                                <div>{member.departement ?? "Sans direction"}</div>
                              </div>
                            </label>
                          );
                        })}
                      </div>
                    ) : (
                      <div className="px-4 py-10 text-center text-sm text-gray-400">
                        Aucun membre actif trouvé.
                      </div>
                    )}
                  </div>
                </div>
              ) : null}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">Description *</label>
                <textarea
                  value={form.description}
                  onChange={(event) => setForm({ ...form, description: event.target.value })}
                  placeholder="Description de l'événement..."
                  rows={3}
                  maxLength={500}
                  className="w-full px-4 py-3 border border-gray-200 rounded-xl text-sm focus:outline-none focus:border-[#E8521A] transition-colors resize-none"
                />
              </div>
              {isChildrenEventForm ? (
                <>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">Image de l&apos;événement</label>
                    <div className="flex flex-wrap gap-2 mb-3">
                      <button
                        type="button"
                        onClick={() => setImageMode("url")}
                        className={`px-4 py-2 rounded-xl text-sm font-medium transition-colors ${
                          imageMode === "url" ? "bg-[#E8521A] text-white" : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                        }`}
                      >
                        URL HTTP
                      </button>
                      <button
                        type="button"
                        onClick={() => setImageMode("upload")}
                        className={`px-4 py-2 rounded-xl text-sm font-medium transition-colors ${
                          imageMode === "upload" ? "bg-[#E8521A] text-white" : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                        }`}
                      >
                        Choisir sur l&apos;ordinateur
                      </button>
                    </div>

                    {imageMode === "url" ? (
                      <>
                        <input
                          type="url"
                          value={form.image}
                          onChange={(event) => {
                            setForm({ ...form, image: event.target.value });
                            setImagePreviewUrl(event.target.value || null);
                          }}
                          placeholder="https://..."
                          className="w-full px-4 py-3 border border-gray-200 rounded-xl text-sm focus:outline-none focus:border-[#E8521A] transition-colors"
                        />
                        <p className="mt-1 text-xs text-gray-400">Ce champ est optionnel. Tu peux aussi publier l&apos;événement sans image.</p>
                      </>
                    ) : (
                      <>
                        <input
                          key={imageInputKey}
                          type="file"
                          accept="image/*"
                          onChange={(event) => {
                            const file = event.target.files?.[0] ?? null;
                            setSelectedImageFile(file);
                            if (file) {
                              setForm({ ...form, image: "" });
                            }
                          }}
                          className="block w-full text-sm text-gray-500 file:mr-4 file:rounded-xl file:border-0 file:bg-orange-50 file:px-4 file:py-2.5 file:text-sm file:font-medium file:text-[#E8521A] hover:file:bg-orange-100"
                        />
                        <p className="mt-1 text-xs text-gray-400">Optionnel. Si tu ajoutes une image, elle sera envoyée sur Cloudinary au moment de l&apos;enregistrement.</p>
                      </>
                    )}
                  </div>
                  {imagePreviewUrl ? (
                    <div className="overflow-hidden rounded-xl border border-gray-100">
                      <img src={imagePreviewUrl} alt="Aperçu de l'événement" className="h-40 w-full object-cover" />
                    </div>
                  ) : null}
                </>
              ) : null}
              {formError ? (
                <div className="rounded-xl border border-red-100 bg-red-50 px-4 py-3 text-sm text-red-600">
                  {formError}
                </div>
              ) : null}
            </div>
            <div className="p-6 border-t border-gray-100 flex flex-col-reverse sm:flex-row gap-3">
              <button onClick={() => setShowForm(false)} className="flex-1 py-3 rounded-xl border border-gray-200 text-gray-600 text-sm font-medium hover:bg-gray-50 transition-colors">
                Annuler
              </button>
              <button onClick={() => void handleSave()} className="flex-1 py-3 rounded-xl bg-[#E8521A] text-white text-sm font-semibold hover:bg-[#d44a16] transition-colors">
                {editEvent ? "Mettre à jour" : isBureauMeetingForm ? "Planifier" : "Créer"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
